import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { LANGUAGES, TIME_ZONES } from '@/components/admin/options';
import { copyToClipboard } from '@/components/admin/util';
import { UserPlus, MoreVertical, KeyRound, Copy, Power, PowerOff, Pencil, Users, MapPin } from 'lucide-react';

const nativeSelect = 'w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-slate-700 bg-white';

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

const RequestorManager = () => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [requestors, setRequestors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [maxPerLoc, setMaxPerLoc] = useState(2);
  const [formOpen, setFormOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);

  const load = useCallback(async () => {
    const [r, l, s] = await Promise.all([
      axios.get(`${API}/client/requestors`, authHeaders),
      axios.get(`${API}/client/locations`, authHeaders),
      axios.get(`${API}/client/settings`, authHeaders).catch(() => ({ data: { max_requestors_per_location: 2 } })),
    ]);
    setRequestors(r.data || []);
    setLocations(l.data || []);
    setMaxPerLoc(s.data?.max_requestors_per_location ?? 2);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const locName = (id) => locations.find((l) => l.id === id)?.location_name || id;

  const setStatus = async (u, status) => {
    try {
      await axios.patch(`${API}/client/requestors/${u.id}/status`, { status }, authHeaders);
      toast.success(`Requestor ${status === 'Active' ? 'activated' : 'deactivated'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change status'); }
  };

  return (
    <div data-testid="requestor-manager">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5FA718]">Facilities Director</div>
          <h1 className="text-2xl font-extrabold text-[#0E2A47]">Location Requestors</h1>
          <p className="text-sm text-slate-500">Manage who can submit requests for your locations (max {maxPerLoc} per location).</p>
        </div>
        <button onClick={() => { setEditUser(null); setFormOpen(true); }} data-testid="create-requestor-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white hover:bg-[#16324F]">
          <UserPlus size={15} /> Add Requestor
        </button>
      </div>

      <Card>
        <CardContent className="p-0 divide-y divide-gray-100">
          {requestors.map((u) => (
            <div key={u.id} data-testid={`requestor-row-${u.id}`} className="flex items-center gap-3 px-5 py-4">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0E2A47] text-xs font-bold text-white shrink-0">
                {(u.full_name || u.name || '?').replace(/[^a-zA-Z ]/g, '').trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate">{u.full_name || u.name}</div>
                <div className="text-xs text-slate-400 truncate">{u.email}{u.job_title ? ` · ${u.job_title}` : ''}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(u.assigned_location_ids || []).map((id) => (
                    <span key={id} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">
                      <MapPin size={9} /> {locName(id)}
                    </span>
                  ))}
                </div>
              </div>
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.status === 'Inactive' ? 'bg-slate-200 text-slate-500' : 'bg-green-100 text-green-700'}`} data-testid={`requestor-status-${u.id}`}>
                {u.status || 'Active'}
              </span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="rounded-md p-1.5 hover:bg-gray-100 text-slate-500" data-testid={`requestor-actions-${u.id}`}><MoreVertical size={16} /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem onClick={() => { setEditUser(u); setFormOpen(true); }} data-testid={`requestor-edit-${u.id}`}><Pencil size={14} className="mr-2" /> Edit</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setPwUser(u)} data-testid={`requestor-resetpw-${u.id}`}><KeyRound size={14} className="mr-2" /> Reset Password</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {u.status === 'Inactive' ? (
                    <DropdownMenuItem onClick={() => setStatus(u, 'Active')} data-testid={`requestor-activate-${u.id}`}><Power size={14} className="mr-2" /> Activate</DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => setStatus(u, 'Inactive')} className="text-amber-700" data-testid={`requestor-deactivate-${u.id}`}><PowerOff size={14} className="mr-2" /> Deactivate</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
          {requestors.length === 0 && (
            <div className="py-16 text-center text-slate-400 text-sm"><Users size={30} className="mx-auto mb-2 text-slate-300" /> No requestors yet. Add one to let your team submit requests.</div>
          )}
        </CardContent>
      </Card>

      {formOpen && (
        <RequestorForm onClose={() => setFormOpen(false)} onSaved={load}
          editUser={editUser} locations={locations} maxPerLoc={maxPerLoc} requestors={requestors} />
      )}
      {pwUser && <ResetPwDialog user={pwUser} onClose={() => setPwUser(null)} />}
    </div>
  );
};

const RequestorForm = ({ onClose, onSaved, editUser, locations, maxPerLoc, requestors }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const isEdit = !!editUser;
  const [form, setForm] = useState({
    full_name: '', email: '', job_title: '', mobile_number: '', office_number: '',
    assigned_location_ids: [], preferred_language: 'English', time_zone: 'America/New_York',
    auto_generate_password: true, password: '', must_change_password: true,
    ...(editUser ? {
      full_name: editUser.full_name || editUser.name || '', email: editUser.email || '',
      job_title: editUser.job_title || '', mobile_number: editUser.mobile_number || '',
      office_number: editUser.office_number || '', assigned_location_ids: editUser.assigned_location_ids || [],
      preferred_language: editUser.preferred_language || 'English', time_zone: editUser.time_zone || 'America/New_York',
    } : {}),
  });
  const [busy, setBusy] = useState(false);
  const [genPw, setGenPw] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // count active requestors per location (excluding the one being edited) to disable full locations
  const capReached = (id) => {
    const n = requestors.filter((r) => r.status !== 'Inactive' && r.id !== editUser?.id && (r.assigned_location_ids || []).includes(id)).length;
    return n >= maxPerLoc;
  };
  const toggleLoc = (id) => {
    if (!form.assigned_location_ids.includes(id) && capReached(id)) {
      toast.error('Maximum authorized requestors reached for this location.');
      return;
    }
    set('assigned_location_ids', form.assigned_location_ids.includes(id)
      ? form.assigned_location_ids.filter((x) => x !== id) : [...form.assigned_location_ids, id]);
  };

  const submit = async () => {
    if (!form.full_name.trim()) return toast.error('Full name is required');
    if (!isEdit && !form.email.trim()) return toast.error('Email is required');
    if (form.assigned_location_ids.length === 0) return toast.error('Assign at least one Service Location');
    setBusy(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/client/requestors/${editUser.id}`, {
          full_name: form.full_name, job_title: form.job_title, mobile_number: form.mobile_number,
          office_number: form.office_number, assigned_location_ids: form.assigned_location_ids,
          preferred_language: form.preferred_language, time_zone: form.time_zone,
        }, authHeaders);
        toast.success('Requestor updated'); onSaved(); onClose();
      } else {
        const r = await axios.post(`${API}/client/requestors`, {
          full_name: form.full_name, email: form.email, job_title: form.job_title,
          mobile_number: form.mobile_number, office_number: form.office_number,
          assigned_location_ids: form.assigned_location_ids,
          preferred_language: form.preferred_language, time_zone: form.time_zone,
          auto_generate_password: form.auto_generate_password,
          password: form.auto_generate_password ? null : form.password,
          must_change_password: form.must_change_password,
        }, authHeaders);
        onSaved();
        if (r.data?.generated_password) { setGenPw(r.data.generated_password); toast.success('Requestor created'); }
        else { toast.success('Requestor created'); onClose(); }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="requestor-form-dialog">
        <DialogHeader><DialogTitle className="text-[#0E2A47]">{isEdit ? `Edit ${editUser.full_name || editUser.name}` : 'Add Location Requestor'}</DialogTitle></DialogHeader>
        {genPw ? (
          <div className="py-4" data-testid="requestor-genpw-panel">
            <div className="rounded-lg border border-[#E8C99B] bg-[#FBF3E4] p-5 text-center">
              <KeyRound className="mx-auto text-[#C8641E] mb-2" size={26} />
              <p className="text-sm text-slate-600 mb-2">Temporary password — share it securely with the requestor.</p>
              <div className="flex items-center justify-center gap-2">
                <code className="rounded bg-white px-4 py-2 font-mono text-lg font-bold text-[#0E2A47] border" data-testid="requestor-genpw-value">{genPw}</code>
                <button onClick={() => copyToClipboard(genPw)} className="rounded-md border border-gray-200 p-2 hover:bg-gray-50"><Copy size={16} /></button>
              </div>
            </div>
            <DialogFooter className="mt-4"><button onClick={onClose} className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white" data-testid="requestor-genpw-done">Done</button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name *"><Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} data-testid="rf-fullname" /></Field>
              <Field label="Email *" hint={isEdit ? 'Email cannot be changed' : 'Primary communication channel'}>
                <Input type="email" value={form.email} disabled={isEdit} onChange={(e) => set('email', e.target.value)} data-testid="rf-email" />
              </Field>
              <Field label="Job Title"><Input value={form.job_title} onChange={(e) => set('job_title', e.target.value)} data-testid="rf-jobtitle" /></Field>
              <Field label="Mobile Number" hint="Stored as (###) ###-#### for future SMS"><Input value={form.mobile_number} onChange={(e) => set('mobile_number', e.target.value)} data-testid="rf-mobile" /></Field>
              <Field label="Office Number"><Input value={form.office_number} onChange={(e) => set('office_number', e.target.value)} data-testid="rf-office" /></Field>
              <Field label="Time Zone">
                <select className={nativeSelect} value={form.time_zone} onChange={(e) => set('time_zone', e.target.value)} data-testid="rf-timezone">
                  {TIME_ZONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Assigned Service Location(s) *" hint={`Only your Customer's locations · max ${maxPerLoc} requestors each`}>
              <div className="max-h-40 overflow-y-auto rounded-md border border-gray-200 p-2 space-y-1" data-testid="rf-locations">
                {locations.map((l) => {
                  const on = form.assigned_location_ids.includes(l.id);
                  const full = !on && capReached(l.id);
                  return (
                    <label key={l.id} className={`flex items-center gap-2 text-sm rounded px-1 py-0.5 ${full ? 'opacity-70' : 'cursor-pointer hover:bg-gray-50'}`}>
                      <input type="checkbox" checked={on} disabled={full} onChange={() => toggleLoc(l.id)} data-testid={`rf-loc-${l.id}`} />
                      <span className="text-slate-700">{l.location_name}</span>
                      {full && <span className="text-[10px] text-red-500 font-medium" data-testid={`rf-loc-full-${l.id}`}>Maximum authorized requestors reached for this location.</span>}
                    </label>
                  );
                })}
                {locations.length === 0 && <p className="text-[11px] text-slate-400">No service locations under your Customer.</p>}
              </div>
            </Field>
            {!isEdit && (
              <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><KeyRound size={15} /> Temporary Password</div>
                <div className="flex items-center gap-2">
                  <Switch checked={form.auto_generate_password} onCheckedChange={(v) => set('auto_generate_password', v)} data-testid="rf-autogen" />
                  <span className="text-sm text-slate-600">Auto-generate a secure password</span>
                </div>
                {!form.auto_generate_password && <Input type="text" placeholder="Temporary password" value={form.password} onChange={(e) => set('password', e.target.value)} data-testid="rf-password" />}
                <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={form.must_change_password} onChange={(e) => set('must_change_password', e.target.checked)} data-testid="rf-mustchange" />
                  Remind them to change it on next login
                </label>
              </div>
            )}
            <DialogFooter className="gap-2">
              <button onClick={onClose} className="rounded-md border border-gray-200 px-5 py-2 text-sm font-semibold text-slate-600">Cancel</button>
              <button onClick={submit} disabled={busy} className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="rf-submit">{busy ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Requestor'}</button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ResetPwDialog = ({ user, onClose }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [autoGen, setAutoGen] = useState(true);
  const [pw, setPw] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/client/requestors/${user.id}/password`, { auto_generate: autoGen, password: autoGen ? null : pw, must_change_password: true }, authHeaders);
      if (r.data?.generated_password) setResult(r.data.generated_password);
      else { toast.success('Password updated'); onClose(); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reset'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="requestor-resetpw-dialog">
        <DialogHeader><DialogTitle className="text-[#0E2A47]">Reset password — {user.full_name || user.name}</DialogTitle></DialogHeader>
        {result ? (
          <div className="py-2">
            <div className="rounded-lg border border-[#E8C99B] bg-[#FBF3E4] p-5 text-center">
              <p className="text-sm text-slate-600 mb-2">New temporary password:</p>
              <div className="flex items-center justify-center gap-2">
                <code className="rounded bg-white px-4 py-2 font-mono text-lg font-bold text-[#0E2A47] border" data-testid="requestor-reset-value">{result}</code>
                <button onClick={() => copyToClipboard(result)} className="rounded-md border border-gray-200 p-2 hover:bg-white"><Copy size={16} /></button>
              </div>
            </div>
            <DialogFooter className="mt-4"><button onClick={onClose} className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white" data-testid="requestor-reset-done">Done</button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2"><Switch checked={autoGen} onCheckedChange={setAutoGen} data-testid="requestor-reset-autogen" /><span className="text-sm text-slate-600">Auto-generate</span></div>
            {!autoGen && <Input type="text" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="requestor-reset-input" />}
            <DialogFooter className="gap-2">
              <button onClick={onClose} className="rounded-md border border-gray-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
              <button onClick={submit} disabled={busy} className="rounded-md bg-[#0E2A47] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="requestor-reset-submit">{busy ? 'Working…' : 'Reset'}</button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RequestorManager;
